import { getConfig, klinikenApi } from "@/api";
import { convertLakemedelData } from "@/store/modules/lakemedel/helpers";
import { convertHandelsvaraData } from "@/tabs/Receptforskrivning/helpers";
import {
    Forskrivningsstatus,
    Lakemedel,
    Receptforskrivning,
    ReceptforskrivningDisplay,
} from "@/tabs/Receptforskrivning/types";
import moment from "moment";

export const fetchForskrivningar: (args: {
    patientinfo: any;
    headers: { [key: string]: string };
    localAccess?: boolean;
}) => Promise<{
    lakemedel: Lakemedel[];
    forskrivningar: { [key: string]: ReceptforskrivningDisplay[] };
}> = async ({ patientinfo, headers, localAccess }) => {
    if (!patientinfo) {
        return {
            lakemedel: [] as Lakemedel[],
            forskrivningar: {},
        };
    }

    try {
        const { data } = await klinikenApi.get<Receptforskrivning[]>(
            `nll/forskrivningar/${patientinfo?.patient?.kliniken_id}/${localAccess ? "lokala/" : ""}`,
            getConfig({
                headers,
            })
        );

        /**
         * Skickas med i return för att kunna stoppa in i store
         */
        const retrievedLakemedel: Lakemedel[] = [];

        /**
         * Ser till att rätt data från läkemedel/handelsvara sätts i förskrivningarna
         */
        const mappedForskrivningar = data.map(({ lakemedel, handelsvara, ...singleData }) => {
            if (lakemedel) {
                const modifiedLakemedel = {
                    ...lakemedel,
                    id: lakemedel.nplPackId,
                };
                retrievedLakemedel.push(modifiedLakemedel);

                const lakemedelDisplay = convertLakemedelData({
                    lakemedel: modifiedLakemedel,
                });

                return {
                    ...singleData,
                    lakemedelId: lakemedel.nplPackId,
                    nplId: lakemedel.nplId,
                    kvarvarande_mangd: singleData.kvarvarande_mangd,
                    mangd:
                        singleData.antal_uttag && lakemedelDisplay?.mangd && singleData.forpackningar_per_uttag
                            ? Number(
                                  singleData.antal_uttag * lakemedelDisplay.mangd * singleData.forpackningar_per_uttag
                              )
                            : undefined,
                    styrka: lakemedelDisplay?.styrka,
                    substans: lakemedelDisplay?.substans,
                    atc: lakemedelDisplay?.atc,
                    form: lakemedelDisplay?.form,
                    lakemedelInfo: lakemedelDisplay?.lakemedel,
                    uttag_kvar: `${singleData.antal_uttag_kvar ?? 0} av ${singleData.antal_uttag ?? 0}`,
                    forpackningsinnehall: lakemedelDisplay?.forpackning,
                    linkFass: lakemedelDisplay?.linkFass,
                    ikoner: {
                        kontrollerat: lakemedelDisplay?.kontrollerat,
                        narkotikaNiva: lakemedelDisplay?.narkotikaNiva,
                        sparrar: singleData?.sparrar,
                        forskrivningsstatus: singleData?.forskrivningsstatus,
                    },
                    isHandelsvara: false,
                    utfardandedatumDate: moment(singleData.utfardandedatum).format("YYYY-MM-DD"),
                };
            }

            if (handelsvara) {
                const handelsvaraDisplay = convertHandelsvaraData({ handelsvara });

                return {
                    ...singleData,
                    styrka: handelsvaraDisplay?.styrka,
                    lakemedelInfo: handelsvaraDisplay?.lakemedelInfo,
                    uttag_kvar: `${singleData.antal_uttag_kvar ?? 0} av ${singleData.antal_uttag ?? 0}`,
                    kvarvarande_mangd: singleData.kvarvarande_mangd,
                    mangd:
                        singleData.antal_uttag && handelsvaraDisplay?.mangd && singleData.forpackningar_per_uttag
                            ? Number(
                                  singleData.antal_uttag * handelsvaraDisplay.mangd * singleData.forpackningar_per_uttag
                              )
                            : undefined,
                    forpackningsinnehall: handelsvaraDisplay?.forpackning,
                    sparrar: singleData?.sparrar,
                    ikoner: {
                        sparrar: singleData?.sparrar,
                    },
                    isHandelsvara: true,
                    utfardandedatumDate: moment(singleData.utfardandedatum).format("YYYY-MM-DD"),
                };
            }

            return {};
        });

        return {
            /**
             * Läkemedel utplockade ur hämtade förskrivningar. Stoppas in i store
             */
            lakemedel: retrievedLakemedel,
            /**
             * Grupperar förskrivningar på kedjeid för att senare kunna avgöra vilka grupper av förskrivningar som
             * ska skrivas ut efter varandra i listan
             */
            // @ts-ignore
            forskrivningar: Object.groupBy(mappedForskrivningar, (forskrivning: Receptforskrivning) => {
                return forskrivning.forskrivningskedja_id ?? "none";
            }),
        };
    } catch (error) {
        console.log(error);

        throw error;
    }
};

const sortForskrivningar = (
    a: ReceptforskrivningDisplay | ReceptforskrivningDisplay[],
    b: ReceptforskrivningDisplay | ReceptforskrivningDisplay[]
) => {
    const compareA: ReceptforskrivningDisplay = Array.isArray(a) ? a[0] : a;
    const compareB: ReceptforskrivningDisplay = Array.isArray(b) ? b[0] : b;

    if (compareA.utfardandedatum === compareB.utfardandedatum) {
        return 0;
    }

    return compareA.utfardandedatum > compareB.utfardandedatum ? -1 : 1;
};

const addKedja = ({
    forskrivning,
    index,
}: {
    forskrivning: ReceptforskrivningDisplay;
    index: number;
}): ReceptforskrivningDisplay => {
    return {
        ...forskrivning,
        kedja: true,
        ikoner: {
            ...(forskrivning.ikoner ?? {}),
            kedja: true,
            kedjaIndex: index,
        },
    } as ReceptforskrivningDisplay;
};

const addParallell = ({
    forskrivning,
    otherId,
}: {
    forskrivning: ReceptforskrivningDisplay;
    otherId: string | null;
}): ReceptforskrivningDisplay => {
    return {
        ...forskrivning,
        parallell: otherId,
        ikoner: {
            ...(forskrivning.ikoner ?? {}),
            parallell: true,
        },
    } as ReceptforskrivningDisplay;
};

export const getStructuredForskrivningar = ({
    forskrivningar,
    localAccess,
}: {
    forskrivningar: { [key: string]: ReceptforskrivningDisplay[] };
    localAccess: boolean;
}) => {
    const aktiva: Array<ReceptforskrivningDisplay | ReceptforskrivningDisplay[]> = [];
    const historiska: Array<ReceptforskrivningDisplay | ReceptforskrivningDisplay[]> = [];
    const papper: Array<ReceptforskrivningDisplay | ReceptforskrivningDisplay[]> = [];

    /**
     * Används för att sätta "id" på förskrivningar inom en kedja och rendera bredvid ikonen i tabellen
     */
    let kedjaIndex = 1;

    Object.entries(forskrivningar).forEach(([forskrivningskedja_id, forskrivningarKedja]) => {
        // Om det inte finns något kedjeid
        if (forskrivningskedja_id === "none") {
            forskrivningarKedja.forEach((forskrivning) => {
                if (forskrivning.uttag_pappersrecept) {
                    papper.push(forskrivning);
                } else if (
                    [Forskrivningsstatus.Aktiv, Forskrivningsstatus.Parkerad].includes(forskrivning.forskrivningsstatus)
                ) {
                    aktiva.push(forskrivning);
                } else {
                    historiska.push(forskrivning);
                }
            });
        } else if (forskrivningarKedja.length === 1) {
            // Om kedjan bara innehåller 1 förskrivning (förskrivning kan räknas som del av kedja, även om det bara
            // är 1)
            const forskrivning = forskrivningarKedja[0];

            if (forskrivning.uttag_pappersrecept) {
                papper.push(forskrivning);
            } else if (
                [Forskrivningsstatus.Aktiv, Forskrivningsstatus.Parkerad].includes(forskrivning.forskrivningsstatus)
            ) {
                aktiva.push(forskrivning);
            } else {
                historiska.push(forskrivning);
            }
        } else if (
            forskrivningarKedja.find((forskrivning) =>
                [Forskrivningsstatus.Aktiv, Forskrivningsstatus.Parkerad].includes(forskrivning.forskrivningsstatus)
            ) &&
            !localAccess
        ) {
            // Om det är en kedja med fler förskrivningar och minst 1 av dem är status aktiv/parkerad
            const aktivKedja: ReceptforskrivningDisplay[] = [];
            const historiskKedja: ReceptforskrivningDisplay[] = [];
            const papperKedja: ReceptforskrivningDisplay[] = [];
            forskrivningarKedja.forEach((forskrivning) => {
                if (forskrivning.uttag_pappersrecept) {
                    papperKedja.push(forskrivning);
                } else if (
                    [
                        Forskrivningsstatus.Aktiv,
                        Forskrivningsstatus.Parkerad,
                        Forskrivningsstatus.Slutexpedierad,
                    ].includes(forskrivning.forskrivningsstatus)
                ) {
                    // Stoppar in i aktiva eller historiska listan baserat på förskrivningars status
                    aktivKedja.push(
                        addKedja({
                            forskrivning,
                            index: kedjaIndex,
                        })
                    );
                } else {
                    historiskKedja.push(
                        addKedja({
                            forskrivning,
                            index: kedjaIndex,
                        })
                    );
                }
            });

            if (aktivKedja.length) {
                // Endast kedja med parallella förskrivningar kan ha fler än 1 i aktiva
                if (aktivKedja.length > 1) {
                    aktiva.push(
                        aktivKedja
                            .map((forskrivning, idx, arr) => {
                                // Hitta ID:t på den andra aktiva förskrivningen i kedjan som denna är parallell med
                                const other = arr.find((_, i) => i !== idx);
                                return addParallell({
                                    forskrivning,
                                    otherId: other?.id ?? null,
                                });
                            })
                            .reverse()
                    );
                } else {
                    aktiva.push(aktivKedja);
                }
            }

            if (historiskKedja.length) {
                historiska.push(historiskKedja.reverse());
            }

            if (papperKedja.length) {
                papper.push(papperKedja.reverse());
            }

            if (aktivKedja.length || historiskKedja.length) {
                kedjaIndex++;
            }
        } else {
            historiska.push(
                forskrivningarKedja.map((forskrivning) => addKedja({ forskrivning, index: kedjaIndex })).reverse()
            );

            kedjaIndex++;
        }
    });

    aktiva.sort(sortForskrivningar);
    historiska.sort(sortForskrivningar);
    papper.sort(sortForskrivningar);

    return {
        aktiva: aktiva.flat(),
        historiska: historiska.flat(),
        papper: papper.flat(),
    };
};
